import { createRouter, createWebHistory } from 'vue-router';
import mqtt from '@/plugins/mqtt';
import paths from './paths';
import logger from '@/plugins/logger';
import useAuthStore from '@/store/modules/pinia/auth';

function route({ path, view, name, meta, props }) {
  return {
    name: name || view,
    path,
    meta,
    component: () => import(`@/views/${view}.vue`),
    props,
  };
}

const isRefreshed = (from) => !from.name;

const router = createRouter({
  history: createWebHistory(),
  routes: paths.map((path) => route(path)),
});

const originalBack = router.back.bind(router);
const AUTH_ENTRY_PATHS = ['/login'];
const MFA_PATH_PREFIX = '/mfa';
const HOME_ROUTE = { name: 'Home' };

function getPathWithoutQueryAndHash(path = '') {
  return path.split(/[?#]/)[0];
}

function isAuthEntryPath(path) {
  return AUTH_ENTRY_PATHS.includes(path) || path.startsWith(MFA_PATH_PREFIX);
}

async function refreshSessionOnHardReload(authStore, from) {
  if (!isRefreshed(from) || authStore.isLoggedIn()) {
    return;
  }

  try {
    await authStore.checkUserLogged();
    if (authStore.isLoggedIn()) {
      mqtt.connect();
    }
  } catch (e) {
    logger.error('Error checking user authentication', e);
  }
}

function shouldFallbackFromBack(backPath, currentPath) {
  if (!backPath) {
    return true;
  }

  if (backPath === currentPath) {
    return true;
  }

  // Ignore query/hash so MFA/login checks work for URLs like /mfa/validate?foo=1.
  const backPathWithoutQuery = getPathWithoutQueryAndHash(backPath);

  return isAuthEntryPath(backPathWithoutQuery);
}

// Centralized safe back navigation for pages that use router.back().
router.back = () => {
  const backPath = window.history.state?.back;
  const currentPath = router.currentRoute.value?.fullPath;

  if (shouldFallbackFromBack(backPath, currentPath)) {
    router.push(HOME_ROUTE).catch(() => {});
    return;
  }

  originalBack();
};

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  const isAuthEntryRoute = isAuthEntryPath(to.path);
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  if (isAuthEntryRoute || requiresAuth) {
    await refreshSessionOnHardReload(authStore, from);
  }

  // Treat login and all MFA routes as auth-entry screens.

  if (isAuthEntryRoute) {
    // Logged-in users should not be able to revisit login/MFA screens.
    // Allow navigation to login when a critical service is unavailable
    // (e.g. MQTT went offline and triggered a logout redirect).
    const serviceUnavailable = authStore.serviceUnavailable?.isUnavailable;
    if (authStore.isLoggedIn() && !serviceUnavailable) {
      return next(HOME_ROUTE);
    }
  }

  if (requiresAuth) {
    if (authStore.isLoggedIn()) {
      return next();
    }

    if (
      to.path.includes('password/reset') ||
      to.path.includes('reset-credentials') ||
      to.path.includes('activate') ||
      !authStore.isLoggedIn()
    ) {
      return next({
        path: '/login',
        query: { redirect: to.fullPath },
      });
    }
    return next();
  }

  return next();
});

export default router;
